<script setup>
/**
 * EconomicGoalChart
 * Donut/pie chart showing agent's economic goal progress
 */
import { computed } from 'vue'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['click'])

// Calculate circumference for SVG circle (radius 40)
const circumference = computed(() => 2 * Math.PI * 40)

// Calculate stroke dashoffset based on percentage
const strokeDashoffset = computed(() => {
  const percentage = props.data.analys || 0
  return circumference.value - (percentage / 100) * circumference.value
})

// Format percentage display
const percentageDisplay = computed(() => {
  return (props.data.analys || 0).toFixed(2) + '%'
})

// Format amounts for display
const amountDisplay = computed(() => {
  const amount = props.data.amount || 0
  return amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
})

const goalDisplay = computed(() => {
  const goal = props.data.goal || 0
  return goal.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
})

function handleClick() {
  emit('click')
}
</script>

<template>
  <div class="economic-chart" @click="handleClick">
    <div class="chart-title">יעד כלכלי - {{ data.name }}</div>

    <div class="svg-wrapper">
      <svg width="200" height="200" viewBox="0 0 100 100" class="pie-chart">
        <!-- Background Circle -->
        <circle r="40" cx="50" cy="50" class="bg-circle"></circle>
        <!-- Progress Circle -->
        <circle
          r="40"
          cx="50"
          cy="50"
          class="progress-circle"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="strokeDashoffset"
        ></circle>
      </svg>
      <div class="percentage-label">{{ percentageDisplay }}</div>
    </div>

    <div class="chart-subtitle">
      <span class="amount">{{ amountDisplay }} &#8362;</span>
      <span class="separator">/</span>
      <span class="goal">{{ goalDisplay }} &#8362;</span>
    </div>

    <div class="click-hint">לחץ לפרטים</div>
  </div>
</template>

<style scoped>
.economic-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-lg);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
  height: 100%;
  min-height: 300px;
  box-sizing: border-box;
}

.economic-chart:hover {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-md);
}

.chart-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
}

.svg-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
}

.pie-chart {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.bg-circle {
  fill: none;
  stroke: var(--bg-tertiary);
  stroke-width: 8;
}

.progress-circle {
  fill: none;
  stroke: #0088fe;
  stroke-width: 8;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease;
}

.percentage-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.chart-subtitle {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-md);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.amount {
  color: var(--text-primary);
}

.separator {
  color: var(--text-muted);
}

.goal {
  color: #0088fe;
}

.click-hint {
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.economic-chart:hover .click-hint {
  opacity: 1;
}

/* Responsive */
@media (max-width: 850px) {
  .svg-wrapper {
    width: 160px;
    height: 160px;
  }

  .chart-title {
    font-size: var(--font-size-base);
  }

  .percentage-label {
    font-size: var(--font-size-lg);
  }

  .chart-subtitle {
    font-size: var(--font-size-sm);
  }
}

@media (max-width: 680px) {
  .svg-wrapper {
    width: 180px;
    height: 180px;
  }
}
</style>
